import React, { memo, FC, useEffect } from 'react';
import styles from '@/styles/components/select-drawer.module.scss';
import { Drawer, Checkbox, Space, Radio, Divider } from 'antd';
import classNames from 'classnames';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppState } from '@/store';
import {
  changeDataType,
  changeEndTimestamp,
  changeIsOpen,
  changeProductSelected,
  changeStartTimestamp,
  changeTopicSelected
} from '@/store/slices/sortFilterSlice';
import dayjs from 'dayjs';
import Image from 'next/image';
import { getCateTagsAction, getProductTagsAction } from '@/store/actions/categoryPageAction';

interface Props {
  tags: string;
  category?: string;
  onSearch: () => void;
  language: any;
}

const SelectDrawer: FC<Props> = memo(props => {
  const { category, onSearch, language } = props;

  const dispatch = useDispatch<any>();
  const { cateTags, productTags, isOpen, topicSelected, productSelected, dataType } = useSelector(
    (state: AppState) => ({
      cateTags: state.categoryPage.cateTags,
      productTags: state.categoryPage.productTags,

      isOpen: state.sortFilter.isOpen,
      topicSelected: state.sortFilter.topicTagEntryIds,
      productSelected: state.sortFilter.prodTagEntryIds,
      dataType: state.sortFilter.dataType
    }),
    shallowEqual
  );

  // Handle Topic
  const handleChangeTopic = (cateTitle: string, topicUid: string) => {
    const newTopicSelected = topicSelected.includes(topicUid)
      ? topicSelected.filter(topic => topic !== topicUid)
      : [...topicSelected, topicUid];

    dispatch(changeTopicSelected(newTopicSelected));
  };

  const changeSelectedAllTopic = (cateTitle: string) => {
    const cateTag = cateTags.find((cateTag: any) => cateTag.title === cateTitle);
    const cateTagTopics = cateTag.topics.map((topic: any) => topic.uid);
    const selectedAll = cateTagTopics.every((uid: string) => topicSelected.includes(uid));

    const newTopicSelected = !selectedAll
      ? [
          ...topicSelected,
          ...cateTagTopics.filter((cateTagTopic: string) => !topicSelected.includes(cateTagTopic))
        ]
      : topicSelected.filter((cateTagTopic: string) => !cateTagTopics.includes(cateTagTopic));
    dispatch(changeTopicSelected(newTopicSelected));
  };

  const handleSearch = () => {
    onSearch && onSearch();
  };

  const onReset = async () => {
    dispatch(changeTopicSelected([]));
    dispatch(changeProductSelected([]));
    dispatch(changeStartTimestamp(0));
    dispatch(changeEndTimestamp(0));
    dispatch(changeDataType(1));
  };

  const changeDateFilter = (type: number) => {
    dispatch(changeDataType(type));
    const timestamp = Math.floor(dayjs().valueOf() / 1000);
    switch (type) {
      case 1:
        dispatch(changeStartTimestamp(0));
        dispatch(changeEndTimestamp(timestamp));
        break;
      case 2:
        const thirtyDaysAgo = Math.floor(dayjs().subtract(30, 'day').valueOf() / 1000);
        dispatch(changeStartTimestamp(thirtyDaysAgo));
        dispatch(changeEndTimestamp(timestamp));
        break;
      case 3:
        const threeMonthsAgo = Math.floor(dayjs().subtract(3, 'month').valueOf() / 1000);
        dispatch(changeStartTimestamp(threeMonthsAgo));
        dispatch(changeEndTimestamp(timestamp));
        break;
      case 4:
        const oneYearAgo = Math.floor(dayjs().subtract(1, 'year').valueOf() / 1000);
        dispatch(changeStartTimestamp(oneYearAgo));
        dispatch(changeEndTimestamp(timestamp));
        break;
      default:
        return;
    }
  };

  let filterCateTags =
    Array.isArray(cateTags) && category
      ? cateTags.filter((item: any) => item.title === category)
      : cateTags;

  let languageText = {
    select: '選擇',
    topicTag: '主題類別',
    selectAll: '全選',
    productTag: '產品類別',
    allProducts: '所有產品',
    dateRange: '日期範圍',
    anyTime: '不限時間',
    pastDays: '最近30天',
    pastMonth: '最近三個月',
    pastYear: '最近一年',
    reset: '重設',
    displayBlog: '顯示網誌'
  };
  if (language === 'en') {
    languageText = {
      select: 'Select',
      topicTag: 'Topic Tag',
      selectAll: 'Select all',
      productTag: 'Product Tag',
      allProducts: 'All products',
      dateRange: 'Date Range',
      anyTime: 'Any time',
      pastDays: 'Past 30 days',
      pastMonth: 'Past 3 months',
      pastYear: 'Past 1 year',
      reset: 'Reset',
      displayBlog: 'Show articles'
    };
  }

  // useEffect(() => {
  // 	if (isOpen) {
  // 		const localLanguage = language === 'en' ?'en-us' : 'zh-hk'
  // 		dispatch(getCateTagsAction(localLanguage));
  // 		dispatch(getProductTagsAction(localLanguage));
  // 	}
  // }, [isOpen]);

  return (
    <Drawer
      open={isOpen}
      className={styles.Chose_Drawer}
      placement="bottom"
      height="100%"
      // headerStyle={{ display: 'none' }}
      styles={{ header: { display: 'none' } }}
    >
      <div
        className={classNames(
          styles.Wrapper,
          language === 'zh' ? styles.Wrapper_ZH : styles.Wrapper_EN
        )}
      >
        <div className={styles.Cancel_Btn_Wrapper} onClick={() => dispatch(changeIsOpen(false))}>
          <div className={styles.Cancel_Btn}>
            <Image
              src="/blog/icon/Close.svg"
              alt={''}
              width={10}
              height={10}
              className={styles.Close}
            />
            <Image
              src="/blog/icon/CloseWhite.svg"
              alt={''}
              width={10}
              height={10}
              className={styles.Close_White}
            />
          </div>
        </div>
        <div className={styles.Main_Title}>{languageText.select}</div>
        <div className={styles.First_Title}>{languageText.topicTag}</div>
        {Array.isArray(filterCateTags) &&
          filterCateTags.map((category: any) => (
            <div key={category.title} className={styles.Second_Wrapper}>
              <div className={styles.Second_Title}>
                <div className={styles.title}>{category.topics[0].category[0].title}</div>
                <Checkbox
                  checked={category.topics
                    .map((topic: any) => topic.uid)
                    .every((uid: string) => topicSelected.includes(uid))}
                  onClick={() => changeSelectedAllTopic(category.title)}
                >
                  {languageText.selectAll}
                </Checkbox>
              </div>
              <Space size={[8, 8]} wrap className={styles.Items_Wrapper}>
                {category.topics.map((topic: any) => (
                  <span
                    key={topic.uid}
                    onClick={() => handleChangeTopic(category.title, topic.uid)}
                    className={classNames({
                      [styles.Select_Items]: topicSelected.includes(topic.uid)
                    })}
                  >
                    {topic.title}
                  </span>
                ))}
              </Space>
            </div>
          ))}

        <Divider></Divider>

        <div className={styles.Date_Wrapper}>
          <div className={styles.First_Title}>{languageText.dateRange}</div>
          <Radio.Group onChange={e => changeDateFilter(e.target.value)} value={dataType}>
            <Radio className={styles.Radio} value={1}>
              {languageText.anyTime}
            </Radio>
            <Radio className={styles.Radio} value={2}>
              {languageText.pastDays}
            </Radio>
            <Radio className={styles.Radio} value={3}>
              {languageText.pastMonth}
            </Radio>
            <Radio className={styles.Radio} value={4}>
              {languageText.pastYear}
            </Radio>
          </Radio.Group>
        </div>

        <Divider></Divider>

        <div className={styles.Btn_Wrapper}>
          <div className={styles.Center_Btn}>
            <button className={styles.Show_Blog_Btn} onClick={handleSearch}>
              {languageText.displayBlog}
            </button>
            <div className={styles.Redo_Wrapper} onClick={onReset}>
              <Image src="/blog/icon/Redo.svg" alt={''} width={12} height={11} />{' '}
              {languageText.reset}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
});

export default SelectDrawer;
